(function initHeaderToolbar() {
    'use strict';

    const TOOLBAR_SELECTOR =
        '[data-header-toolbar]';

    const CLOSE_SELECTOR =
        '[data-header-toolbar-close]';

    const MESSAGES_SELECTOR =
        '[data-toolbar-messages]';

    const MESSAGE_SELECTOR =
        '[data-toolbar-message]';

    const DEFAULT_STORAGE_KEY =
        'header-toolbar-dismissed';

    const DEFAULT_DURATION =
        5000;

    const TRANSITION_DURATION =
        450;

    const toolbarTimers =
        new WeakMap();

    /**
     * Obtiene la key utilizada para recordar
     * que el toolbar fue cerrado.
     */
    function getStorageKey(toolbar) {
        return (
            toolbar.dataset.toolbarStorageKey ||
            DEFAULT_STORAGE_KEY
        );
    }

    /**
     * Comprueba si el toolbar ya fue cerrado
     * durante esta sesión.
     */
    function isDismissed(toolbar) {
        try {
            return (
                window.sessionStorage.getItem(
                    getStorageKey(toolbar)
                ) === 'true'
            );
        } catch (error) {
            return false;
        }
    }

    /**
     * Guarda el estado cerrado durante
     * toda la sesión/pestaña actual.
     */
    function saveDismissed(toolbar) {
        try {
            window.sessionStorage.setItem(
                getStorageKey(toolbar),
                'true'
            );
        } catch (error) {
            console.warn(
                '[HeaderToolbar] No fue posible guardar el estado.',
                error
            );
        }
    }

    /**
     * Fuerza al theme a volver a calcular
     * las dimensiones del header.
     */
    function refreshHeaderMeasurements() {
        window.requestAnimationFrame(() => {
            window.dispatchEvent(
                new Event('resize')
            );

            document.dispatchEvent(
                new CustomEvent(
                    'theme:resize'
                )
            );
        });
    }

    /**
     * Detiene cualquier timer activo del toolbar.
     */
    function stopMessages(toolbar) {
        const timer =
            toolbarTimers.get(toolbar);

        if (timer) {
            window.clearTimeout(timer);

            toolbarTimers.delete(
                toolbar
            );
        }
    }

    /**
     * Oculta físicamente el toolbar.
     */
    function hideToolbar(toolbar) {
        if (!toolbar) {
            return;
        }

        stopMessages(toolbar);

        toolbar.classList.add(
            'is-dismissed'
        );

        toolbar.hidden = true;

        toolbar.setAttribute(
            'aria-hidden',
            'true'
        );

        document.documentElement.classList.add(
            'header-toolbar-dismissed'
        );

        refreshHeaderMeasurements();
    }

    /**
     * Devuelve la duración configurada.
     */
    function getMessageDuration(
        messagesContainer
    ) {
        const duration =
            Number(
                messagesContainer.dataset
                    .messageDuration
            );

        if (
            !Number.isFinite(duration) ||
            duration <= 0
        ) {
            return DEFAULT_DURATION;
        }

        return duration;
    }

    /**
     * Cambia de un mensaje al siguiente.
     */
    function showNextMessage(
        toolbar,
        messagesContainer
    ) {
        const messages =
            Array.from(
                messagesContainer.querySelectorAll(
                    MESSAGE_SELECTOR
                )
            );

        if (messages.length <= 1) {
            return;
        }

        let currentIndex =
            messages.findIndex(
                (message) =>
                    message.classList.contains(
                        'is-active'
                    )
            );

        if (currentIndex < 0) {
            currentIndex = 0;

            messages[0].classList.add(
                'is-active'
            );
        }

        const nextIndex =
            (currentIndex + 1) %
            messages.length;

        const currentMessage =
            messages[currentIndex];

        const nextMessage =
            messages[nextIndex];

        /*
         * Preparamos el siguiente debajo
         * del viewport.
         */
        nextMessage.classList.remove(
            'is-active',
            'is-leaving',
            'is-entering'
        );

        /*
         * Fuerza al navegador a registrar
         * el estado inicial translateY(100%).
         */
        void nextMessage.offsetHeight;

        currentMessage.classList.add(
            'is-leaving'
        );

        nextMessage.classList.add(
            'is-entering'
        );

        window.setTimeout(() => {
            currentMessage.classList.remove(
                'is-active',
                'is-leaving'
            );

            nextMessage.classList.remove(
                'is-entering'
            );

            nextMessage.classList.add(
                'is-active'
            );
        }, TRANSITION_DURATION);
    }

    /**
     * Programa la rotación infinita.
     */
    function scheduleNextMessage(
        toolbar,
        messagesContainer
    ) {
        stopMessages(toolbar);

        const messages =
            messagesContainer.querySelectorAll(
                MESSAGE_SELECTOR
            );

        if (messages.length <= 1) {
            return;
        }

        const duration =
            getMessageDuration(
                messagesContainer
            );

        const rotate = () => {
            if (
                toolbar.hidden ||
                isDismissed(toolbar)
            ) {
                stopMessages(toolbar);

                return;
            }

            showNextMessage(
                toolbar,
                messagesContainer
            );

            const timer =
                window.setTimeout(
                    rotate,
                    duration
                );

            toolbarTimers.set(
                toolbar,
                timer
            );
        };

        const timer =
            window.setTimeout(
                rotate,
                duration
            );

        toolbarTimers.set(
            toolbar,
            timer
        );
    }

    /**
     * Inicializa el slider de mensajes.
     */
    function initializeMessages(
        toolbar
    ) {
        const messagesContainer =
            toolbar.querySelector(
                MESSAGES_SELECTOR
            );

        if (!messagesContainer) {
            return;
        }

        const messages =
            Array.from(
                messagesContainer.querySelectorAll(
                    MESSAGE_SELECTOR
                )
            );

        if (!messages.length) {
            return;
        }

        /*
         * Restablecemos estado para evitar
         * inconsistencias al recargar la
         * sección desde Theme Editor.
         */
        messages.forEach(
            (
                message,
                index
            ) => {
                message.classList.remove(
                    'is-active',
                    'is-leaving',
                    'is-entering'
                );

                if (index === 0) {
                    message.classList.add(
                        'is-active'
                    );
                }
            }
        );

        scheduleNextMessage(
            toolbar,
            messagesContainer
        );
    }

    /**
     * Inicializa los toolbars existentes.
     */
    function initializeToolbars(
        root = document
    ) {
        root
            .querySelectorAll(
                TOOLBAR_SELECTOR
            )
            .forEach(
                (toolbar) => {
                    stopMessages(toolbar);

                    if (
                        isDismissed(toolbar)
                    ) {
                        hideToolbar(toolbar);

                        return;
                    }

                    initializeMessages(
                        toolbar
                    );
                }
            );
    }

    /**
     * Delegación global del botón cerrar.
     */
    document.addEventListener(
        'click',
        (event) => {
            const closeButton =
                event.target.closest(
                    CLOSE_SELECTOR
                );

            if (!closeButton) {
                return;
            }

            const toolbar =
                closeButton.closest(
                    TOOLBAR_SELECTOR
                );

            if (!toolbar) {
                console.warn(
                    '[HeaderToolbar] No se encontró el toolbar padre.'
                );

                return;
            }

            event.preventDefault();
            event.stopPropagation();

            saveDismissed(toolbar);
            hideToolbar(toolbar);
        }
    );

    /**
     * Primera carga.
     */
    if (
        document.readyState ===
        'loading'
    ) {
        document.addEventListener(
            'DOMContentLoaded',
            () => {
                initializeToolbars(
                    document
                );
            },
            {
                once: true,
            }
        );
    } else {
        initializeToolbars(
            document
        );
    }

    /**
     * Theme Editor.
     */
    document.addEventListener(
        'shopify:section:load',
        (event) => {
            initializeToolbars(
                event.target
            );
        }
    );

    /**
     * Detener timers cuando Shopify
     * elimina una sección.
     */
    document.addEventListener(
        'shopify:section:unload',
        (event) => {
            event.target
                .querySelectorAll(
                    TOOLBAR_SELECTOR
                )
                .forEach(
                    (toolbar) => {
                        stopMessages(
                            toolbar
                        );
                    }
                );
        }
    );
})();